import { FieldData, InternalNamePath, ValidateErrorEntity } from "@rc-component/form/es/interface";
import { TimeRangePickerProps } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { FormInstance } from "antd/lib/form";
import dayjs, { Dayjs } from "dayjs";
import { AsYouType } from "libphonenumber-js";
import t from "../../app/i18n";
import type { FieldConstraintViolation } from "../types";
import { formatFourSpacedString, formatLicensePlate } from "./formatUtils";
import { tValidationMessage } from "./translationUtils";
import { isDeepEqual, isLocalhostDevMode, parseBirthDateFromPin, stripAccents } from "./utils";
import { validationFunctions } from "./validationUtils";

export const setErrorsToForm = (
  form: FormInstance,
  translationRootPath: string,
  violations?: FieldConstraintViolation[],
) => {
  if (!violations?.length) {
    return;
  }

  const registeredNames = form.getFieldsError().map((field) => field.name);

  const errorFields = violations.flatMap<FieldData>((violation) => {
    const name = findRegisteredName(registeredNames, violation.fieldPath);

    return name
      ? [
          {
            name,
            errors: violation.errors.map((errorMessage) => tValidationMessage(translationRootPath, errorMessage)),
          },
        ]
      : [];
  });

  if (errorFields.length > 0) {
    form.setFields(errorFields);
  }
};

/**
 * Backend paths carry no segment types ("entries[1].endDate", "byYear[2024]"), while the form matches
 * names strictly (list index is a number, object key a string), so the path is resolved against the
 * names the form actually registered.
 */
const findRegisteredName = (registeredNames: InternalNamePath[], fieldPath: string) => {
  const segments = fieldPath.split(/[.[\]]/).filter(Boolean);

  return registeredNames.find(
    (name) => name.length === segments.length && name.every((segment, index) => String(segment) === segments[index]),
  );
};

/**
 * Validating a name in @rc-component/form returns the values of every field nested under it, so a parent
 * such as a Form.List would bring fields excluded by the caller back into the submitted values.
 */
export const getAllFieldsNames = (form: FormInstance) => {
  const names = form.getFieldsError().map((field) => field.name);

  return names.filter(
    (name) =>
      !names.some((other) => other.length > name.length && name.every((segment, index) => other[index] === segment)),
  );
};

export const resolveFormValidationError = (errorInfo: ValidateErrorEntity) => {
  if (isLocalhostDevMode()) {
    console.warn("Form validation error caught.", errorInfo);
  }
};

export const getDatePickerFormat = () => [
  dayjs.localeData().longDateFormat("L"),
  dayjs.localeData().longDateFormat("l"),
  "DD. MM. YYYY",
  "D. M. YYYY",
];

export const getDateTimePickerFormat = () =>
  dayjs.localeData().longDateFormat("L") + " " + dayjs.localeData().longDateFormat("LTS");

const getRestrictedDateRange = ({
  startDate,
  endDate,
  minDate,
  maxDate,
}: {
  startDate: Dayjs;
  endDate: Dayjs;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}): [Dayjs, Dayjs] => [
  minDate && startDate.isBefore(minDate) ? minDate : startDate,
  maxDate && endDate.isAfter(maxDate) ? maxDate : endDate,
];

export const getDatePickerDefaultRanges = ({
  minDate,
  maxDate,
  hasToday,
  hasNextMonth,
}: {
  minDate?: Dayjs;
  maxDate?: Dayjs;
  hasToday?: boolean;
  hasNextMonth?: boolean;
} = {}): TimeRangePickerProps["presets"] => {
  const result: TimeRangePickerProps["presets"] = [
    {
      label: t("common.thisWeek"),
      value: getRestrictedDateRange({
        startDate: dayjs().startOf("week"),
        endDate: dayjs().endOf("week"),
        minDate,
        maxDate,
      }),
    },
    {
      label: t("common.thisMonth"),
      value: getRestrictedDateRange({
        startDate: dayjs().startOf("month"),
        endDate: dayjs().endOf("month"),
        minDate,
        maxDate,
      }),
    },
    {
      label: t("common.lastMonth"),
      value: getRestrictedDateRange({
        startDate: dayjs().subtract(1, "month").startOf("month"),
        endDate: dayjs().subtract(1, "month").endOf("month"),
        minDate,
        maxDate,
      }),
    },
  ];

  if (hasToday) {
    result.unshift({
      label: t("common.today"),
      value: getRestrictedDateRange({
        startDate: dayjs().startOf("day"),
        endDate: dayjs().endOf("day"),
        minDate,
        maxDate,
      }),
    });
  }

  if (hasNextMonth) {
    result.push({
      label: t("common.nextMonth"),
      value: getRestrictedDateRange({
        startDate: dayjs().add(1, "month").startOf("month"),
        endDate: dayjs().add(1, "month").endOf("month"),
        minDate,
        maxDate,
      }),
    });
  }

  return result;
};

export const selectFilterFunction = (input: string, option?: DefaultOptionType) => {
  if (!option) {
    return false;
  }

  return (
    stripAccents((option.label?.toString() || option.children?.toString())?.toLowerCase()).indexOf(
      stripAccents(input.toLowerCase()),
    ) !== -1
  );
};

export const selectTagsFilterFunction = (
  input: string,
  translationsMap: Map<string | number, string>,
  option?: DefaultOptionType,
) => {
  if (!option) {
    return false;
  }

  return stripAccents((option.value ? translationsMap.get(option.value.toString()) : "")?.toLowerCase()).includes(
    stripAccents(input.toLowerCase()),
  );
};

export const treeNodeFilterFunction = (input: string, treeNode: Record<string, any>) => {
  return stripAccents(treeNode["title"].toLowerCase()).indexOf(stripAccents(input.toLowerCase())) !== -1;
};

export const upperCaseStringNormalizeFunction = (input: string) => (input ? input.toUpperCase() : input);

export const licensePlateNormalizeFunction = (input?: string) => (input ? formatLicensePlate(input) : undefined);

export const phoneNumberNormalizeFunction = (value?: string) => (value ? new AsYouType("SK").input(value) : value);

export const ibanNormalizeFunction = (input?: string) => (input ? formatFourSpacedString(input).toUpperCase() : input);

export const fillFormBirthDateFromPin = (pin: string, form: FormInstance, birthDateFormKey: string = "birthDate") => {
  if (!pin || !validationFunctions.validatePin(pin)) {
    form.setFieldsValue({ [birthDateFormKey]: undefined });
  } else {
    form.setFieldsValue({ [birthDateFormKey]: parseBirthDateFromPin(pin) });
  }
};

type ConvertObjectToFormData = {
  object: Record<string, any>;
  formData?: FormData;
  parentKey?: string;
  filesNameProp?: string[];
};

export const convertObjectToFormData = ({
  object,
  formData = new FormData(),
  parentKey = "",
  filesNameProp = ["file", "files"],
}: ConvertObjectToFormData) => {
  Object.entries(object).forEach(([key, value]) => {
    const formKey = parentKey ? `${parentKey}[${key}]` : key;

    if (typeof value === "object" && "uid" in value && value.uid.startsWith("rc-upload")) {
      formData.append(parentKey, value);
    } else if (value instanceof File) {
      formData.append(formKey, value);
    } else if (value instanceof Blob) {
      formData.append(formKey, value, "blob");
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        convertObjectToFormData({ object: { [index]: item }, formData, parentKey: formKey, filesNameProp });
      });
    } else if (typeof value === "object" && value !== null) {
      convertObjectToFormData({ object: value, formData, parentKey: formKey, filesNameProp });
    } else if (value !== undefined && value !== null) {
      formData.append(formKey, value);
    }
  });

  return formData;
};

export const convertObjectToBlob = (object: Record<string, any>) => {
  return new Blob([JSON.stringify(object)], { type: "application/json" });
};

export const skipInitialFormValues = <T>(formValues: Partial<T>, initialValues: T) => {
  const result: Partial<T> = {};

  Object.entries(formValues).forEach(([key, value]) => {
    result[key as keyof T] = isDeepEqual(initialValues[key as keyof T], value) ? undefined : formValues[key as keyof T];
  });

  return result;
};
